// shabba-dashboard/src/components/analytics/LiquidityHeatmap.tsx

import React from 'react';
import type { LiquidityHeatmap as LiquidityHeatmapType } from '../../types';

interface LiquidityHeatmapProps {
  data: LiquidityHeatmapType;
}

export function LiquidityHeatmap({ data }: LiquidityHeatmapProps) {
  const { priceLevels, maxLiquidity, avgLiquidity } = data;

  const getHeatColor = (heatScore: number) => {
    if (heatScore === 0) return '#1e293b';
    if (heatScore < 0.2) return '#0f4c75';
    if (heatScore < 0.4) return '#3282b8';
    if (heatScore < 0.6) return '#0ea5e9';
    if (heatScore < 0.8) return '#f59e0b';
    return '#ef4444';
  };

  const getTextColor = (heatScore: number) => {
    return heatScore > 0.5 ? 'white' : '#e2e8f0';
  };

  if (priceLevels.length === 0) {
    return (
      <div style={{ 
        backgroundColor: '#1e293b', 
        padding: '16px', 
        borderRadius: '8px', 
        border: '1px solid #334155',
        textAlign: 'center',
        color: '#94a3b8'
      }}>
        No liquidity data available
      </div>
    );
  }

  return (
    <div style={{ 
      backgroundColor: '#1e293b', 
      padding: '16px', 
      borderRadius: '8px', 
      border: '1px solid #334155' 
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h4 style={{ margin: '0', fontSize: '14px', fontWeight: '600', color: '#e2e8f0' }}>
          Liquidity Heatmap
        </h4>
        <div style={{ fontSize: '11px', color: '#94a3b8' }}>
          Max: {maxLiquidity.toLocaleString()} | Avg: {avgLiquidity.toLocaleString()}
        </div>
      </div>

      {/* Heat Legend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <span style={{ fontSize: '11px', color: '#94a3b8' }}>Low</span>
        <div style={{ display: 'flex', gap: '2px' }}>
          {[0, 0.2, 0.4, 0.6, 0.8, 1].map((score, i) => (
            <div
              key={i}
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: getHeatColor(score),
                borderRadius: '2px'
              }}
            />
          ))}
        </div>
        <span style={{ fontSize: '11px', color: '#94a3b8' }}>High</span>
      </div>

      {/* Price Levels Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', 
        gap: '4px',
        maxHeight: '300px',
        overflowY: 'auto'
      }}>
        {priceLevels.map((level, index) => (
          <div
            key={index}
            style={{
              backgroundColor: getHeatColor(level.heatScore),
              padding: '8px 6px',
              borderRadius: '4px',
              border: '1px solid #334155',
              textAlign: 'center',
              minHeight: '60px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}
          >
            <div style={{ 
              fontSize: '10px', 
              color: getTextColor(level.heatScore),
              fontWeight: '600'
            }}>
              {level.price.toFixed(2)}
            </div>
            
            <div style={{ fontSize: '9px', color: getTextColor(level.heatScore) }}>
              <div style={{ color: '#10b981' }}>B: {level.bidLiquidity.toLocaleString()}</div>
              <div style={{ color: '#eabc14ff' }}>A: {level.askLiquidity.toLocaleString()}</div>
            </div>
            
            <div style={{ 
              fontSize: '8px', 
              color: getTextColor(level.heatScore),
              opacity: 0.8
            }}>
              {level.totalLiquidity.toLocaleString()}
            </div>
          </div>
        ))}
      </div>

      {/* Summary Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(3, 1fr)', 
        gap: '8px', 
        marginTop: '12px',
        padding: '8px',
        backgroundColor: '#334155',
        borderRadius: '4px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '10px', color: '#94a3b8' }}>Levels</div>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#e2e8f0' }}>
            {priceLevels.length}
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '10px', color: '#94a3b8' }}>Max Heat</div>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#e2e8f0' }}>
            {(Math.max(...priceLevels.map(l => l.heatScore)) * 100).toFixed(0)}%
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '10px', color: '#94a3b8' }}>Avg Heat</div>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#e2e8f0' }}>
            {(priceLevels.reduce((sum, l) => sum + l.heatScore, 0) / priceLevels.length * 100).toFixed(0)}%
          </div>
        </div>
      </div>
    </div>
  );
}
